import { Component } from 'react'
import cx from 'classnames'
import { Link } from 'react-router'
import { APP_URL } from 'constants/ResourcesConstants'
import ACCESS from 'utils/Access'
import 'styles/components/settings/Settings.styl'
import NavAccordion from './NavAccordion'
import { CUTOVER_SETTINGS_ROUTES } from './Routing/constants'
import { Icon } from '@driftt/tide-core-legacy'
import { connect } from 'react-redux'
import { settingsAppConfig } from 'app/modules/user/selectors'
import { getHasUpdatedSettingsSidebar } from './cutover'
import * as OrgSelectors from "../../app/modules/org/selectors"
import ArrowLeftIcon from './arrow-1-left.svg'
import ArrowRightIcon from './arrow-1-right.svg'
import Subnav from '../shared/Subnav'
import SettingsPageHeader from './SettingsPageHeader'
import { PAGE_TITLES } from './constants'
import { ensureIsAdmin } from 'app/modules/access/helpers'

const SettingsItem = ({ itemPath, pathTest, title, locked }) => {
  const relativePath = itemPath.replace('/settings', '')
  const pathObj = CUTOVER_SETTINGS_ROUTES.find(function(route) {
    return route.path === relativePath
  })
  const shouldRedirect = pathObj && (!pathObj.requiredGate || ACCESS.hasGates(pathObj.requiredGate))

  const linkProps = shouldRedirect ? { href: `${APP_URL}/settings2${relativePath}` } : { to: `${itemPath}` }

  return (
    <Link
      className={cx({ selected: pathTest.test(window.location.pathname) }, 'settings-navigation-item')}
      activeClassName="settings-navigation-item-selected"
      {...linkProps}
    >
      {title}
      {locked && <Icon name="lock" width={16} height={16} className="nav-locked-icon"/>}
    </Link>
  )
}

const PERSONAL_SETTINGS_PATHS = [
  '/settings/account',
  '/settings/notifications',
  '/settings/profile',
  '/settings/calendar',
  '/settings/email',
  '/settings/assistant',
]

const TEAM_SETTINGS_PATHS = ['/settings/company', '/settings/team', '/settings/teams', '/settings/rules']
const MEETINGS_PATHS = ['/settings/calendar', '/settings/calendar/advanced', '/settings/calendar/connect']

class Settings extends Component {
  constructor(props) {
    super(props)
    this.state = {
      showMySettings: PERSONAL_SETTINGS_PATHS.includes(window.location.pathname),
      showOrganizationSettings: TEAM_SETTINGS_PATHS.includes(window.location.pathname),
      hideSettingsNav: MEETINGS_PATHS.includes(window.location.pathname),
      hasUpdatedSettingsSidebarCutover: undefined,
      navHidden: false,
      pageTitle: this.getSettingsPageTitle(),
    }
  }

  componentDidMount() {
    this.getHasUpdatedSettingsSidebarCutover()
  }

  componentWillReceiveProps = nextProps => {
    if (this.props.location.pathname !== nextProps.location.pathname) {
      this.setState({ hideSettingsNav: MEETINGS_PATHS.includes(window.location.pathname) })
    }
  }

  componentDidUpdate = prevProps => {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.setState({
        pageTitle: this.getSettingsPageTitle()
      })
    }
  }

  getSettingsPageTitle = () => {
    const { location: { pathname }} = this.props
    const pathObj = PAGE_TITLES.find((element => element.path === pathname))
    if (pathObj) { return pathObj.title }
    return false
  }

  getHasUpdatedSettingsSidebarCutover() {
    const { orgId } = this.props
    getHasUpdatedSettingsSidebar(orgId)
      .then(result => this.setState({
        hasUpdatedSettingsSidebarCutover: result.UPDATED_SETTINGS_SIDEBAR
      }))
      .catch((error) => {
        console.error(error)
      })
  }

  toggleShowMySettings = () => {
    this.setState({
      showMySettings: !this.state.showMySettings,
    })
  }

  toggleShowOrganizationSettings = () => {
    this.setState({
      showOrganizationSettings: !this.state.showOrganizationSettings,
    })
  }

  toggleNavHidden = () => this.setState(prevState => ({ navHidden: !prevState.navHidden }))

  renderPersonalSettings = () => {
    const { settingsAppConfig: { hasNotifications, hasEmail, hasProfile, hasDriftAssistant }} = this.props
    return (<nav className="settings-navigation-accordion-item">
      <SettingsItem title="Account" itemPath="/settings/account" pathTest={/settings\/account/} />
      {hasNotifications &&
      <SettingsItem
        title="Notifications"
        itemPath="/settings/notifications"
        pathTest={/settings\/notifications/}
      />
      }
      {hasProfile &&
      <SettingsItem
        title="Drift Profile"
        itemPath="/settings/profile"
        pathTest={/settings\/profile/}
      />
      }
      {hasEmail &&
      <SettingsItem
        title="Email"
        itemPath="/settings/email"
        pathTest={/settings\/email/}
      />
      }
      {hasDriftAssistant &&
      <SettingsItem
        title="Drift Assistant"
        itemPath="/settings/assistant"
        pathTest={/settings\/assistant/}
      />
      }
    </nav>)
  }

  renderOrganizationSettings = (isPremium, isCompany) => {
    const hasTeams = ACCESS.hasGates('has_beta_team_view')
    return (
      <nav className="settings-navigation-accordion-item">
        <SettingsItem title="Billing" itemPath="/settings/billing" pathTest={/settings\/billing/} />

        <SettingsItem title="Manage teammates" itemPath="/settings/team" pathTest={/settings\/team$/} />

        {(isCompany || hasTeams) && (
          <SettingsItem title="Teams" itemPath="/settings/teams" pathTest={/settings\/teams/} />
        )}

        <SettingsItem title="Company details" itemPath="/settings/company" pathTest={/settings\/company/} />

        <SettingsItem
          key="privacy"
          itemPath="/settings/consent"
          pathTest={/settings\/consent/}
          title="Data privacy"
        />
        <SettingsItem
          title="Authentication"
          itemPath="/settings/authentication"
          pathTest={/settings\/authentication/}
        />
      </nav>
    )
  }

  renderAppSettings = (isPremium, isAdmin, hasBetaGlobalCalendars) => {
    return (
      <nav className="settings-navigation-accordion-item">
        <SettingsItem title="Drift Widget" itemPath="/settings/widget" pathTest={/settings\/widget/} />

        <SettingsItem
          title="Conversations"
          itemPath="/settings/conversations"
          pathTest={/settings\/((conversations)|(dkim))/}
        />

        <SettingsItem
          key="routing-rules"
          itemPath="/settings/rules"
          pathTest={/settings\/rules/}
          title="Conversation routing"
        />

        <SettingsItem
          key="bot"
          itemPath="/settings/bot"
          pathTest={/settings\/bot/}
          title="Bot"
          locked={!isPremium}
        />

        {isAdmin && hasBetaGlobalCalendars && (
          <SettingsItem
            title="Calendars"
            itemPath="/settings/global/calendars"
            pathTest={/settings\/global\/calendars/}
          />
        )}
      </nav>
    )
  }

  renderHelpDocsLink = () => {
    return (
      <div className={'settings-navigation-help-docs-wrapper flex-space-between'}>
        <a href="http://help.drift.com" target="_blank">
          <div className={'settings-navigation-help-docs'}>Help Docs</div>
        </a>
      </div>
    )
  }

  renderAllSettings = () => {
    const { showMySettings, showOrganizationSettings } = this.state
    const isAdmin = ACCESS.isAdmin()
    const isPremium = ACCESS.hasGates('premium_drift')
    const isCompany = ACCESS.hasGates('premium_enterprise')
    const hasBetaGlobalCalendars = ACCESS.hasGates('has_beta_global_calendars')
    const isCalendarUser = ACCESS.isCalendarUser()
    return (
      <div>
        <nav className="settings-navigation">
          <NavAccordion open={showMySettings && !showOrganizationSettings} title="My settings">
            {this.renderPersonalSettings(isCalendarUser)}
          </NavAccordion>

          {isAdmin && (
            <NavAccordion open={showOrganizationSettings && !showMySettings} title="Organization settings">
              {this.renderOrganizationSettings(isPremium, isCompany)}
            </NavAccordion>
          )}

          {isAdmin && (
            <NavAccordion open={!showMySettings && !showOrganizationSettings} title="App settings">
              {this.renderAppSettings(isPremium, isAdmin, hasBetaGlobalCalendars)}
            </NavAccordion>
          )}

          {isAdmin && (
            <div className="settings-navigation-support-requests">
              <a href="/settings2/integrations">Integrations</a>
            </div>
          )}

          <div className="settings-navigation-support-requests">
            <a href="/settings2/help">Help</a>
          </div>

        </nav>
      </div>
    )
  }

  renderNavToggle = () => {

    const {
      navHidden,
    } = this.state

    return (
      <div className={cx("settings-navigation-toggle-open", { 'nav-hidden': navHidden })} onClick={this.toggleNavHidden}>
        {navHidden
          ? <img src={ArrowRightIcon} alt="arrow-right" />
          : <img src={ArrowLeftIcon} alt="arrow-left" />
        }
      </div>
    )
  }

  createSubnav = () => {
    const { settingsAppConfig: {
      hasProfile,
      hasDriftAssistant,
      hasNotifications,
      hasEmail,
      hasMeetings,
      hasBilling,
      hasManageTeammates,
      hasTeams,
      hasDataPrivacy,
      hasSecurity,
      hasWidget,
      hasConvoRouting,
      hasBot }
    } = this.props

    const isPremium = ACCESS.hasGates('premium_drift')
    const isAdmin = ACCESS.isAdmin()
    const adminNavigationItems = [
      {
        label: 'ORGANIZATION SETTINGS',
        expanded: true,
        nodes: [
          {
            label: 'Plan and billing',
            path: '/settings2/billing',
            external: true,
            hide: !hasBilling,
          },
          {
            label: 'Manage teammates',
            path: '/settings2/team',
            external: true,
            hide: !hasManageTeammates,
          },
          {
            label: 'Teams',
            path: '/settings2/teams',
            external: true,
            hide: !ACCESS.hasGates(['premium_enterprise', 'has_beta_team_view']) || !hasTeams
          },
          {
            label: 'Company details',
            path: '/settings2/company',
            external: true
          },
          {
            label: 'Data privacy',
            path: '/settings2/consent',
            external: true,
            hide: !hasDataPrivacy,
          },
          {
            label: 'Security',
            path: '/settings2/security',
            external: true,
            hide: !hasSecurity,
          }
        ]
      },
      {
        label: 'APP SETTINGS',
        expanded: true,
        nodes: [
          {
            label: 'Drift widget',
            path: '/settings2/widget',
            external: true,
            hide: !hasWidget,
          },
          {
            label: 'Conversations',
            path: '/settings/conversations',
          },
          {
            label: 'Conversation routing',
            path: '/settings2/rules',
            external: true,
            hide: !hasConvoRouting,
          },
          {
            label: 'Bot',
            path: '/settings2/bot',
            external: true,
            icon: !isPremium ? 'lock' : null,
            disabled: !isPremium,
            hide: !hasBot,
          },
          {
            label: 'Calendars',
            path: '/global/calendars',
            hide: !ACCESS.hasGates('has_beta_global_calendars')
          }
        ]
      },
      {
        label: 'Integrations',
        path: '/settings2/integrations',
        external: true
      },
      {
        label: 'Help',
        path: '/settings2/help',
        external: true
      }]
    const sharedNavigationItems = [
      {
        label: 'MY SETTINGS',
        expanded: true,
        nodes: [
          {
            label: 'Account',
            path: '/settings2/account',
            external: true
          },
          {
            label: 'Notifications',
            path: '/settings2/notifications',
            external: true,
            hide: !hasNotifications
          },
          {
            label: 'Meetings',
            path: '/meetings',
            external: true,
            hide: !hasMeetings
          },
          {
            label: 'Drift Profile',
            path: '/settings/profile',
            hide: !hasProfile
          },
          {
            label: 'Email',
            path: '/settings2/email',
            external: true,
            hide: !hasEmail
          },
          {
            label: 'Drift Assistant',
            path: '/settings/assistant',
            hide: !hasDriftAssistant
          }
        ]
      }
    ]

    const subnavItems = [
      ...sharedNavigationItems,
      ...(isAdmin ? adminNavigationItems
         : [{
           label: 'Help Docs',
           path: 'http://help.drift.com',
           target: '_blank',
           rel: 'noopener noreferrer'
         }]
        )
    ]

    return <Subnav
      title="Settings"
      open={true}
      nodes={subnavItems}
    />

  }

  render() {
    const { pageTitle } = this.state

    return (
      <div className="settings-section">
        <div className={cx("settings-rest can-hide-nav", { 'nav-hidden': this.state.navHidden })}>
          {this.createSubnav()}
          <div className="settings-page">
            { pageTitle &&
              <SettingsPageHeader title={pageTitle} />
            }
            <div className="settings-page-content">{this.props.children}</div>
          </div>
        </div>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  settingsAppConfig: settingsAppConfig(state),
  orgId: OrgSelectors.orgId(state)
})

export default connect(mapStateToProps)(Settings)



// WEBPACK FOOTER //
// ./src/scripts/components/settings/Settings.js